import { useLayoutEffect, useState } from 'react';

// scrollWidth rounds, so a label 0.5px too wide still gets an ellipsis; compare the text's real width.
const textOverflows = (span) => {
    const range = document.createRange?.();
    if (!range || typeof range.getBoundingClientRect !== 'function') return false;
    range.selectNodeContents(span);
    return range.getBoundingClientRect().width > span.getBoundingClientRect().width + 0.01;
};

// Returns a ref for a tab bar; scales its labels down until the longest one fits on one line.
// If that needs < 0.7 (Android won't shrink text below its 8px x font-scale floor), the bar goes to two rows.
export default function useFitLabels(deps = []) {
    const [root, setRoot] = useState(null);
    useLayoutEffect(() => {
        if (!root) return undefined;
        let fitting = false;
        const fit = () => {
            if (fitting) return;
            fitting = true;
            const buttons = Array.from(root.querySelectorAll('button'));
            const overflows = () => buttons.some(b => {
                const s = b.querySelector('span');
                return b.scrollWidth > b.clientWidth || (s && (s.scrollWidth > s.clientWidth || textOverflows(s)));
            });
            const fitsDownTo = (floor) => {
                let scale = 1;
                root.style.removeProperty('--tab-label-scale');
                while (overflows()) {
                    if (scale <= floor) return false;
                    scale = Math.round((scale - 0.06) * 100) / 100;
                    root.style.setProperty('--tab-label-scale', String(scale));
                }
                return true;
            };
            root.classList.remove('labels-two-rows');
            if (!fitsDownTo(0.7)) {
                root.classList.add('labels-two-rows');
                fitsDownTo(0.5);
            }
            fitting = false;
        };
        fit();
        if (document.fonts?.ready) document.fonts.ready.then(fit).catch(() => {});
        if (typeof ResizeObserver === 'undefined') return undefined;
        const observer = new ResizeObserver(fit);
        observer.observe(root);
        root.querySelectorAll('button span').forEach(s => observer.observe(s));
        return () => observer.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [root, ...deps]);
    return setRoot;
}
